import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserPlus,
  X,
  BookOpen,
  Layers,
  CreditCard,
  CheckCircle2,
  AlertCircle,
  Building,
  Globe,
  DollarSign,
  User,
  Mail,
  Phone,
  MapPin,
  ShieldCheck,
} from "lucide-react";
import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import { useCoursesQuery, useCourseTracksQuery } from "../../../hooks/queries/useCourseQueries";
import { useAdminUsersQuery } from "../../../hooks/queries/useUserAdminQueries";
import { useManualEnrollmentMutation } from "../../../hooks/queries/useEnrollmentAdminQueries";
import { useToast } from "../../../hooks/otherHooks/useToast";

interface ManualEnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ManualEnrollmentModal: React.FC<ManualEnrollmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { addToast } = useToast();

  const [deliveryMode, setDeliveryMode] = useState<"online" | "onsite">("online");
  const [curriculumType, setCurriculumType] = useState<"course" | "track">("course");
  const [selectedCurriculumId, setSelectedCurriculumId] = useState<number | "">("");
  const [selectedStudentId, setSelectedStudentId] = useState<number | "">("");

  // Onsite direct fields
  const [onsiteName, setOnsiteName] = useState("");
  const [onsiteEmail, setOnsiteEmail] = useState("");
  const [onsitePhone, setOnsitePhone] = useState("");
  const [onsiteAddress, setOnsiteAddress] = useState("");

  // Financial & access terms
  const [tuitionPlan, setTuitionPlan] = useState<"standard" | "custom" | "scholarship">("standard");
  const [customAmount, setCustomAmount] = useState<number | "">("");
  const [paymentMethod, setPaymentMethod] = useState("Admin Override");
  const [isPaid, setIsPaid] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(true);

  // Queries
  const { data: courses = [], isLoading: loadingCourses } = useCoursesQuery();
  const { data: tracks = [], isLoading: loadingTracks } = useCourseTracksQuery();
  const { data: usersData, isLoading: loadingUsers } = useAdminUsersQuery();

  const studentsList = useMemo(() => {
    const list = Array.isArray(usersData?.users) ? usersData.users : [];
    return list;
  }, [usersData?.users]);

  // Selected curriculum details
  const activeCurriculum = useMemo(() => {
    if (!selectedCurriculumId) return null;
    if (curriculumType === "course") {
      return (courses as any[]).find((c) => c.id === Number(selectedCurriculumId)) || null;
    } else {
      return (tracks as any[]).find((t) => t.id === Number(selectedCurriculumId)) || null;
    }
  }, [selectedCurriculumId, curriculumType, courses, tracks]);

  // Computed tuition amount
  const computedAmount = useMemo(() => {
    if (tuitionPlan === "scholarship") return 0;
    if (tuitionPlan === "custom") return Number(customAmount) || 0;
    if (!activeCurriculum) return 0;
    return deliveryMode === "online"
      ? Number(activeCurriculum.online_price) || 0
      : Number(activeCurriculum.onsite_price) || 0;
  }, [tuitionPlan, customAmount, activeCurriculum, deliveryMode]);

  const { mutateAsync: manualEnrollMutation, isPending: isSubmitting } =
    useManualEnrollmentMutation();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCurriculumId) {
      addToast("Please select a Course or Career Track.", "error");
      return;
    }

    if (deliveryMode === "online" && !selectedStudentId) {
      addToast("Please select an enrolled student.", "error");
      return;
    }

    if (deliveryMode === "onsite" && (!onsiteName.trim() || !onsiteEmail.trim())) {
      addToast("Full name and email are required for onsite student registration.", "error");
      return;
    }

    try {
      const res = await manualEnrollMutation({
        delivery_mode: deliveryMode,
        curriculum_type: curriculumType,
        curriculum_id: Number(selectedCurriculumId),
        student_id: deliveryMode === "online" ? Number(selectedStudentId) : undefined,
        full_name: deliveryMode === "onsite" ? onsiteName.trim() : undefined,
        email: deliveryMode === "onsite" ? onsiteEmail.trim() : undefined,
        phone_number: deliveryMode === "onsite" ? onsitePhone.trim() : undefined,
        address: deliveryMode === "onsite" ? onsiteAddress.trim() : undefined,
        is_paid: isPaid,
        is_enrolled: isEnrolled,
        payment_amount: computedAmount,
        payment_method: paymentMethod,
      });

      addToast(res.message || "Student enrolled successfully!", "success");
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Failed to enroll student manually:", err);
      const errorMsg =
        err?.response?.data?.detail ||
        (typeof err?.response?.data === "object"
          ? Object.values(err.response.data)[0]
          : null) ||
        "Failed to provision student enrollment.";
      addToast(String(errorMsg), "error");
    }
  };

  return (
    <AnimatePresence>
      <div className="cht-fixed cht-inset-0 cht-z-50 cht-flex cht-items-center cht-justify-center cht-p-4 cht-bg-black/50 cht-backdrop-blur-sm cht-overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="cht-w-full cht-max-w-2xl cht-bg-white cht-rounded-3xl cht-shadow-2xl cht-border cht-border-gray-100 cht-overflow-hidden cht-my-8"
        >
          {/* Header */}
          <div className="cht-px-8 cht-py-6 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between cht-bg-gray-50/50">
            <div className="cht-flex cht-items-center cht-gap-3">
              <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center">
                <UserPlus size={20} />
              </div>
              <div>
                <h3 className="cht-text-lg cht-font-black cht-text-gray-900 cht-tracking-tight">
                  Direct Student Admissions Provisioning
                </h3>
                <p className="cht-text-xs cht-text-gray-500 cht-font-medium">
                  Enroll registered students or register physical bootcamp cohort participants
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="cht-p-2 cht-text-gray-400 hover:cht-text-gray-600 cht-rounded-full hover:cht-bg-gray-100 cht-transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="cht-p-8 cht-space-y-6">
            {/* Step 1: Delivery Mode Toggle */}
            <div className="cht-space-y-2">
              <label className="cht-text-[11px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                Delivery Format
              </label>
              <div className="cht-grid cht-grid-cols-2 cht-gap-3">
                <button
                  type="button"
                  onClick={() => setDeliveryMode("online")}
                  className={`cht-p-4 cht-rounded-2xl cht-border cht-flex cht-items-center cht-gap-3 cht-transition-all ${
                    deliveryMode === "online"
                      ? "cht-border-[#3e57da] cht-bg-[#3e57da]/5 cht-text-[#3e57da]"
                      : "cht-border-gray-200 hover:cht-border-gray-300 cht-text-gray-600"
                  }`}
                >
                  <Globe size={20} className={deliveryMode === "online" ? "cht-text-[#3e57da]" : "cht-text-gray-400"} />
                  <div className="cht-text-left">
                    <p className="cht-text-xs cht-font-black">Online Learning</p>
                    <p className="cht-text-[10px] cht-text-gray-400 cht-font-medium">Platform LMS & Video Access</p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setDeliveryMode("onsite")}
                  className={`cht-p-4 cht-rounded-2xl cht-border cht-flex cht-items-center cht-gap-3 cht-transition-all ${
                    deliveryMode === "onsite"
                      ? "cht-border-emerald-600 cht-bg-emerald-50/50 cht-text-emerald-700"
                      : "cht-border-gray-200 hover:cht-border-gray-300 cht-text-gray-600"
                  }`}
                >
                  <Building size={20} className={deliveryMode === "onsite" ? "cht-text-emerald-600" : "cht-text-gray-400"} />
                  <div className="cht-text-left">
                    <p className="cht-text-xs cht-font-black">Onsite Bootcamp</p>
                    <p className="cht-text-[10px] cht-text-gray-400 cht-font-medium">Campus Physical Intake</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Step 2: Student Identification */}
            {deliveryMode === "online" ? (
              <div className="cht-space-y-2">
                <label className="cht-text-[11px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                  Select Registered Student Account
                </label>
                <div className="cht-relative">
                  <select
                    value={selectedStudentId}
                    onChange={(e) => setSelectedStudentId(Number(e.target.value) || "")}
                    disabled={loadingUsers}
                    className="cht-w-full cht-px-4 cht-py-3 cht-rounded-xl cht-border cht-border-gray-200 cht-bg-white cht-text-xs cht-font-medium cht-text-gray-800 focus:cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/20 focus:cht-border-[#3e57da]"
                  >
                    <option value="">-- Choose student from directory --</option>
                    {studentsList.map((stu) => (
                      <option key={stu.id} value={stu.id}>
                        {stu.name} ({stu.email}) - {stu.role}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : (
              <div className="cht-space-y-4">
                <label className="cht-text-[11px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                  Onsite Student Contact Record
                </label>
                <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
                  <Input
                    label="Full Name *"
                    placeholder="e.g. Samuel Adekunle"
                    value={onsiteName}
                    onChange={(e) => setOnsiteName(e.target.value)}
                    icon={<User size={15} />}
                  />
                  <Input
                    label="Email Address *"
                    type="email"
                    placeholder="student@gmail.com"
                    value={onsiteEmail}
                    onChange={(e) => setOnsiteEmail(e.target.value)}
                    icon={<Mail size={15} />}
                  />
                </div>
                <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
                  <Input
                    label="Phone Number"
                    placeholder="080 1234 5678"
                    value={onsitePhone}
                    onChange={(e) => setOnsitePhone(e.target.value)}
                    icon={<Phone size={15} />}
                  />
                  <Input
                    label="Residential Address"
                    placeholder="Warri, Delta State"
                    value={onsiteAddress}
                    onChange={(e) => setOnsiteAddress(e.target.value)}
                    icon={<MapPin size={15} />}
                  />
                </div>
              </div>
            )}

            {/* Step 3: Curriculum Selection */}
            <div className="cht-space-y-3">
              <div className="cht-flex cht-items-center cht-justify-between">
                <label className="cht-text-[11px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                  Target Curriculum
                </label>
                <div className="cht-flex cht-gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setCurriculumType("course");
                      setSelectedCurriculumId("");
                    }}
                    className={`cht-px-3 cht-py-1 cht-rounded-lg cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest ${
                      curriculumType === "course"
                        ? "cht-bg-[#3e57da] cht-text-white"
                        : "cht-bg-gray-100 cht-text-gray-600 hover:cht-bg-gray-200"
                    }`}
                  >
                    Courses
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCurriculumType("track");
                      setSelectedCurriculumId("");
                    }}
                    className={`cht-px-3 cht-py-1 cht-rounded-lg cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest ${
                      curriculumType === "track"
                        ? "cht-bg-[#3e57da] cht-text-white"
                        : "cht-bg-gray-100 cht-text-gray-600 hover:cht-bg-gray-200"
                    }`}
                  >
                    Career Tracks
                  </button>
                </div>
              </div>

              <select
                value={selectedCurriculumId}
                onChange={(e) => setSelectedCurriculumId(Number(e.target.value) || "")}
                disabled={loadingCourses || loadingTracks}
                className="cht-w-full cht-px-4 cht-py-3 cht-rounded-xl cht-border cht-border-gray-200 cht-bg-white cht-text-xs cht-font-medium cht-text-gray-800 focus:cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/20 focus:cht-border-[#3e57da]"
              >
                <option value="">
                  -- Select {curriculumType === "course" ? "Course" : "Career Track"} --
                </option>
                {curriculumType === "course"
                  ? (courses as any[]).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title} (Online: ₦{Number(c.online_price || 0).toLocaleString()} | Onsite: ₦
                        {Number(c.onsite_price || 0).toLocaleString()})
                      </option>
                    ))
                  : (tracks as any[]).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.title} (Online: ₦{Number(t.online_price || 0).toLocaleString()} | Onsite: ₦
                        {Number(t.onsite_price || 0).toLocaleString()})
                      </option>
                    ))}
              </select>
            </div>

            {/* Step 4: Tuition Terms & Pricing Override */}
            <div className="cht-space-y-3">
              <label className="cht-text-[11px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                Tuition Billing Configuration
              </label>
              <div className="cht-grid cht-grid-cols-3 cht-gap-2">
                {[
                  { id: "standard", label: "Standard Price", desc: "Catalog fee" },
                  { id: "custom", label: "Custom Discount", desc: "Adjustable rate" },
                  { id: "scholarship", label: "Scholarship", desc: "100% Free / ₦0" },
                ].map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setTuitionPlan(plan.id as any)}
                    className={`cht-p-3 cht-rounded-xl cht-border cht-text-left cht-transition-all ${
                      tuitionPlan === plan.id
                        ? "cht-border-[#3e57da] cht-bg-[#3e57da]/5 cht-text-[#3e57da]"
                        : "cht-border-gray-200 hover:cht-border-gray-300 cht-text-gray-600"
                    }`}
                  >
                    <p className="cht-text-xs cht-font-black">{plan.label}</p>
                    <p className="cht-text-[10px] cht-text-gray-400">{plan.desc}</p>
                  </button>
                ))}
              </div>

              {tuitionPlan === "custom" && (
                <div className="cht-pt-1">
                  <Input
                    label="Custom Agreed Tuition (₦)"
                    type="number"
                    placeholder="Enter customized tuition amount"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(Number(e.target.value) || "")}
                    icon={<DollarSign size={15} />}
                  />
                </div>
              )}

              {/* Amount Summary Ribbon */}
              <div className="cht-p-4 cht-rounded-2xl cht-bg-gray-50 cht-border cht-border-gray-200/60 cht-flex cht-items-center cht-justify-between">
                <div>
                  <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                    Total Invoiced Amount
                  </p>
                  <p className="cht-text-lg cht-font-black cht-text-gray-900">
                    ₦{computedAmount.toLocaleString()}
                  </p>
                </div>
                <div className="cht-w-48">
                  <label className="cht-text-[10px] cht-font-bold cht-text-gray-400">Payment Channel</label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="cht-w-full cht-mt-0.5 cht-px-2.5 cht-py-1.5 cht-rounded-lg cht-border cht-border-gray-200 cht-text-xs cht-font-bold cht-bg-white"
                  >
                    <option value="Admin Override">Admin Override</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="POS Terminal">POS Terminal</option>
                    <option value="Cash Deposit">Cash Deposit</option>
                    <option value="Scholarship">Scholarship / Sponsorship</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Step 5: Immediate Access Toggles */}
            <div className="cht-grid cht-grid-cols-2 cht-gap-3 cht-pt-1">
              <label className="cht-p-3 cht-rounded-xl cht-border cht-border-gray-200 cht-flex cht-items-center cht-gap-3 cht-cursor-pointer hover:cht-bg-gray-50">
                <input
                  type="checkbox"
                  checked={isEnrolled}
                  onChange={(e) => setIsEnrolled(e.target.checked)}
                  className="cht-w-4 cht-h-4 cht-rounded cht-text-[#3e57da] focus:cht-ring-[#3e57da]"
                />
                <div>
                  <p className="cht-text-xs cht-font-bold cht-text-gray-800">Activate Access</p>
                  <p className="cht-text-[10px] cht-text-gray-400">Student can enter syllabus</p>
                </div>
              </label>

              <label className="cht-p-3 cht-rounded-xl cht-border cht-border-gray-200 cht-flex cht-items-center cht-gap-3 cht-cursor-pointer hover:cht-bg-gray-50">
                <input
                  type="checkbox"
                  checked={isPaid}
                  onChange={(e) => setIsPaid(e.target.checked)}
                  className="cht-w-4 cht-h-4 cht-rounded cht-text-emerald-600 focus:cht-ring-emerald-500"
                />
                <div>
                  <p className="cht-text-xs cht-font-bold cht-text-gray-800">Mark Paid / Cleared</p>
                  <p className="cht-text-[10px] cht-text-gray-400">Tuition marked settled</p>
                </div>
              </label>
            </div>

            {/* Actions */}
            <div className="cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-4 cht-border-t cht-border-gray-100">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
                className="cht-rounded-xl cht-text-xs cht-font-bold"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                className="cht-bg-[#3e57da] hover:cht-bg-[#2d41a8] cht-rounded-xl cht-text-xs cht-font-bold cht-px-6"
              >
                {isSubmitting ? "Provisioning..." : "Confirm Admissions Enrollment"}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ManualEnrollmentModal;
